<template>
    <Modal
        v-if="isOpen"
        :isOpen="isOpen"
        :title="`API Token Management - ${serviceName} (${serviceType})`"
        size="3xl"
        class="sm:max-w-full sm:px-2"
        @update:isOpen="closeModal">
        <template #default>
            <AppLoader :force-loading="loading" default-height="100%">
                <!-- Create Token Section -->
                <div class="mb-6">
                    <div class="flex flex-col sm:flex-row sm:items-center justify-between mb-4 space-y-3 sm:space-y-0">
                        <div v-if="!tokens.length && !showCreateToken" class="text-sm text-muted-foreground">
                            No existing tokens.
                            <span v-can-access.permission="[$Permissions.FE_SECURE_WRITE_TENANT_API_TOKENS]"
                                >Use the "Create API Token" button to start creating one.</span
                            >
                        </div>
                        <Button
                            v-if="!showCreateToken"
                            v-can-access.permission="[$Permissions.FE_SECURE_WRITE_TENANT_API_TOKENS]"
                            @click="handleNewTokenClick"
                            class="bg-primary hover:bg-primary">
                            <Plus class="w-4 h-4 mr-2" />
                            Create API Token
                        </Button>
                    </div>
                    <div v-if="showCreateToken" class="p-4 border rounded-lg bg-muted/50 sm:px-2 sm:py-3 space-y-4">
                        <div>
                            <Label for="token-description">Token Description</Label>
                            <Textarea
                                id="token-description"
                                v-model="newTokenDescription"
                                :disabled="createLoading"
                                placeholder="Enter token description"
                                class="mt-2" />
                        </div>
                        <div>
                            <Label for="token-roles">Roles allowed to use the token</Label>
                            <MultiSelect
                                id="token-roles"
                                v-model="selectedRoleIds"
                                :options="availableRoles"
                                :disabled="createLoading"
                                placeholder="Select roles"
                                :option-label="'name'"
                                :track-by="'id'"
                                class="mt-2" />
                        </div>
                        <div class="flex flex-col sm:flex-row gap-3">
                            <Button @click="handleCreateToken" :disabled="createLoading" variant="default"> Create Token </Button>
                            <Button @click="cancelNewToken" :disabled="createLoading" variant="secondary"> Cancel </Button>
                        </div>
                    </div>
                </div>

                <!-- New Token Display Section -->
                <div v-if="showFullToken" class="p-4 mb-4 border rounded-lg bg-muted/50">
                    <div class="space-y-4">
                        <div>
                            <Label for="new-client-id">Client ID</Label>
                            <div class="flex gap-2 mt-2">
                                <Input id="new-client-id" :modelValue="newClientId" readonly class="font-mono text-sm flex-1" />
                                <Button @click="copyClientId(newClientId)" variant="outline" size="sm" class="flex-shrink-0">
                                    <Copy class="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        <div>
                            <Label for="new-token">Token</Label>
                            <div class="flex gap-2 mt-2">
                                <Input id="new-token" :modelValue="newToken" readonly class="font-mono text-sm flex-1" />
                                <Button @click="copyToken(newToken)" variant="outline" size="sm" class="flex-shrink-0">
                                    <Copy class="h-4 w-4" />
                                </Button>
                                <Button @click="closeSecretToken" variant="secondary" size="sm"> Done </Button>
                            </div>
                        </div>
                    </div>
                    <p class="text-xs text-destructive mt-2">This token is shown only once. Make sure to copy it.</p>
                </div>

                <!-- Token List Section -->
                <div class="mb-6">
                    <div
                        v-if="tokens.length"
                        class="flex justify-between items-center mb-4 p-3 hover:bg-muted cursor-pointer rounded-md"
                        @click="toggleTokenCollapse">
                        <h3 class="text-md font-semibold">Existing Tokens</h3>
                        <ChevronDown class="h-5 w-5 text-primary" :class="{ 'transform rotate-180': !tokensCollapsed }" />
                    </div>
                    <div v-if="tokens.length && !tokensCollapsed" class="overflow-y-auto" :class="showCreateToken ? 'max-h-72' : 'max-h-96'">
                        <ul class="flex flex-col gap-2">
                            <li v-for="(token, index) in tokens" :key="token.client_id" class="flex flex-col p-4 border rounded-lg space-y-3">
                                <div class="flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
                                    <div>
                                        <div>
                                            <Label :for="`client-id-${token.client_id}_${index}`">Client ID</Label>
                                            <div class="flex gap-2 mt-2">
                                                <Input
                                                    :id="`${token.client_id}_${index}`"
                                                    :modelValue="token.client_id"
                                                    readonly
                                                    class="font-mono text-sm flex-1" />
                                                <Button @click="copyClientId(token.client_id)" variant="outline" size="sm" class="flex-shrink-0">
                                                    <Copy class="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                        <div class="mt-4">
                                            <Label :for="`token-${token.client_id}`">Token</Label>
                                            <div class="flex items-center gap-2 mt-2">
                                                <Input :id="`token-${token.client_id}`" :modelValue="token.secret" readonly class="font-mono text-sm flex-1" />
                                                <TooltipProvider v-if="token.description">
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button variant="ghost" size="sm">
                                                                <Info class="h-4 w-4" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>{{ token.description }}</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </div>
                                        </div>
                                        <p class="text-xs text-muted-foreground mt-2"><b>Created at</b> {{ token.created_at }}</p>
                                        <p class="text-xs text-muted-foreground">
                                            <b>Created by</b> {{ token.created_by_user?.name }} ({{ token.created_by_user?.email }})
                                        </p>
                                    </div>
                                    <Button
                                        v-can-access.permission="[$Permissions.FE_SECURE_DELETE_TENANT_API_TOKENS]"
                                        @click="confirmDeleteToken(token)"
                                        :disabled="deleteLoading"
                                        variant="ghost"
                                        size="sm"
                                        class="text-destructive hover:text-destructive">
                                        <Trash2 class="w-4 h-4" />
                                        <span class="sr-only">Invalidate token</span>
                                    </Button>
                                </div>
                                <div v-if="token.roles.length" class="mt-3">
                                    <p class="text-xs text-muted-foreground mb-2">
                                        <b>Roles:</b>
                                    </p>
                                    <div class="flex flex-wrap gap-1">
                                        <Badge v-for="role in token.roles" :key="role.id" variant="secondary" class="text-xs">
                                            {{ role.name }}
                                        </Badge>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </AppLoader>
        </template>

        <template #footer>
            <Button @click="closeModal" variant="secondary"> Close </Button>
        </template>
    </Modal>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import Modal from './Modal.vue';
import { useServicesStore } from '@/stores/services.store';
import { useUserStore } from '@/stores/user.store';
import { Plus, Copy, ChevronDown, Trash2, Info } from 'lucide-vue-next';
import { MultiSelect } from '@/components/ui/multi-select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import AppLoader from '@/components/AppLoader.vue';
import { useDeleteConfirmation } from '@/composables/useDialog';
import { toast } from '@/composables/useToast';

const props = defineProps({
    isOpen: Boolean,
    serviceId: String,
    serviceType: String,
    serviceName: String
});

const emit = defineEmits(['update:isOpen']);
const servicesStore = useServicesStore();
const userStore = useUserStore();
const loading = ref(false);
const deleteLoading = ref(false);
const createLoading = ref(false);
const showFullToken = ref(false);
const newToken = ref(null);
const newClientId = ref(null);
const availableRoles = ref([]);
const selectedRoles = ref([]);
const showCreateToken = ref(false);
const newTokenDescription = ref('');
const tokensCollapsed = ref(false); // Default to expanded

onMounted(() => {
    fetchRoles();
    fetchTokens();
});

// Watch for modal opening to refresh tokens
watch(
    () => props.isOpen,
    (newValue) => {
        if (newValue) {
            fetchTokens();
        }
    }
);

const fetchRoles = async () => {
    if (!userStore.roles?.length) {
        await userStore.fetchRoles();
    }

    availableRoles.value = userStore.roles || [];
};

const fetchTokens = async () => {
    loading.value = true;
    try {
        if (!servicesStore.apiTokensPerService?.[props.serviceId]?.length) {
            await servicesStore.fetchApiTokensForAllServices();
        }
    } finally {
        loading.value = false;
    }
};

const tokens = computed(() => {
    const allTokens = servicesStore.apiTokensPerService;
    const serviceTokens = allTokens?.[props.serviceId] || [];
    return serviceTokens;
});

const selectedRoleIds = computed({
    get() {
        return selectedRoles.value.map((role) => role.id);
    },
    set(ids) {
        selectedRoles.value = ids.map((id) => availableRoles.value.find((role) => role.id === id)).filter(Boolean);
    }
});

const handleNewTokenClick = () => {
    showCreateToken.value = true;
    tokensCollapsed.value = true; // Collapse tokens when creating a new token
};

const cancelNewToken = () => {
    showCreateToken.value = false;
    tokensCollapsed.value = false; // Expand tokens when canceling creation
};

const handleCreateToken = async () => {
    if (!selectedRoles.value.length) {
        toast.error('Please select at least one role.');
        return;
    }
    createLoading.value = true;
    try {
        const createdToken = await servicesStore.createApiTokenForService({
            serviceId: props.serviceId,
            roleIds: selectedRoles.value.map((r) => r.id),
            description: newTokenDescription.value
        });

        newToken.value = createdToken.secret;
        newClientId.value = createdToken.client_id;
        showFullToken.value = true;
        toast.success('New API token created.');
        showCreateToken.value = false;
        tokensCollapsed.value = false; // Expand tokens after creation
        await fetchTokens(); // Keep tokens up to date
    } catch (error) {
        toast.error('Failed to create API token.');
    } finally {
        createLoading.value = false;
    }
};

const confirmDeleteToken = async (token) => {
    try {
        await useDeleteConfirmation({
            entity: 'API Token',
            name: token.client_id,
            title: 'Delete API Token',
            message: `Are you sure you want to delete the API Token <strong>"${token.client_id}"</strong>? This action cannot be undone.`,
            onConfirm: async () => {
                deleteLoading.value = true;
                try {
                    await servicesStore.deleteApiTokenForService(token.client_id);
                } catch (error) {
                    toast.error('Failed to invalidate API token.');
                } finally {
                    deleteLoading.value = false;
                }
            }
        });
    } catch (error) {
        // User cancelled or error occurred
        if (error.message) {
            console.error('Delete failed:', error);
        }
    }
};

const copyToken = (token) => {
    navigator.clipboard.writeText(token).then(
        () => toast.success('Token copied to clipboard!'),
        () => toast.error('Failed to copy token.')
    );
};

const copyClientId = (clientId) => {
    navigator.clipboard.writeText(clientId).then(
        () => toast.success('Client ID copied to clipboard!'),
        () => toast.error('Failed to copy client ID.')
    );
};

const closeSecretToken = () => {
    showFullToken.value = false;
    newToken.value = null;
};

const closeModal = () => {
    emit('update:isOpen', false);
    showFullToken.value = false;
    newToken.value = null;
    selectedRoles.value = [];
    newTokenDescription.value = '';
    tokensCollapsed.value = false; // Reset to expanded on close
};

const toggleTokenCollapse = () => {
    tokensCollapsed.value = !tokensCollapsed.value;
};
</script>

<style scoped>
/* Add any additional styles here */
</style>
